import React, { useState, useEffect } from 'react';
import Sidebar from '../components/Sidebar';
import Topbar from '../components/Topbar';
import GlobeVisualization from '../components/GlobeVisualization';
import WeatherPanel from '../components/WeatherPanel';
import SonarPanel from '../components/SonarPanel';
import MissionSummary from '../components/MissionSummary';
import CrewLogs from '../components/CrewLogs';
import AlertsPanel from '../components/AlertsPanel';
import ChartsPanel from '../components/ChartsPanel';
import { Badge } from '../components/ui/badge';
import { getVesselLocation, getCurrentLocationInfo } from '../config/locations';

const Dashboard = () => {
  const [layers, setLayers] = useState({
    depth: true,
    temperature: true,
    salinity: false,
  });

  const [vesselData, setVesselData] = useState(getVesselLocation());
  const locationInfo = getCurrentLocationInfo();

  // Simulate real-time updates
  useEffect(() => {
    const interval = setInterval(() => {
      setVesselData(prev => ({
        ...prev,
        lat: prev.lat + (Math.random() - 0.5) * 0.001,
        lon: prev.lon + (Math.random() - 0.5) * 0.001,
        speed: prev.speed + (Math.random() - 0.5) * 0.2,
        heading: (prev.heading + (Math.random() - 0.5) * 2 + 360) % 360,
      }));
    }, 3000);

    return () => clearInterval(interval);
  }, []);

  const toggleLayer = (layer) => {
    setLayers(prev => ({
      ...prev,
      [layer]: !prev[layer],
    }));
  };

  return (
    <div 
      className="grid grid-cols-1 lg:grid-cols-[auto_1fr] lg:grid-rows-[56px_1fr] min-h-screen bg-background text-foreground"
      data-testid="dashboard-container"
    >
      <Sidebar layers={layers} toggleLayer={toggleLayer} currentRoute="/" />
      <Topbar vesselData={vesselData} />
      <main className="pt-14 lg:pt-0 lg:col-start-2 lg:row-start-2 p-4 md:p-6 relative z-0">
        {/* Location Info Banner */}
        <div className="mb-4 p-4 rounded-lg bg-gradient-to-br from-primary/15 via-primary/10 to-transparent border border-primary/30 shadow-sm" data-testid="location-banner">
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-3">
            {/* Location Info */}
            <div className="flex items-start gap-3">
              <div className="flex-shrink-0 w-10 h-10 rounded-full bg-primary/20 flex items-center justify-center">
                <svg className="h-5 w-5 text-primary" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                </svg>
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-1">
                  <h3 className="text-sm font-semibold text-foreground">Current Location</h3>
                  <Badge variant="outline" className="text-xs border-primary/40 text-primary">Active</Badge>
                </div>
                <p className="text-base font-medium text-primary mb-1">{locationInfo.name}</p>
                <p className="text-xs text-muted-foreground leading-relaxed">{locationInfo.description}</p>
                <p className="text-xs text-muted-foreground/80 font-mono mt-1">{locationInfo.coordinates}</p>
              </div>
            </div>
            
            {/* Developer Info - Hidden on mobile, shown on larger screens */}
            <div className="hidden lg:flex flex-col items-end text-right">
              <p className="text-xs text-muted-foreground/60 mb-1">Developer Config</p>
              <code className="text-[10px] text-primary/70 bg-primary/5 px-2 py-1 rounded border border-primary/20">
                /frontend/src/config/locations.js
              </code>
            </div>
          </div>
        </div>
        
        <div className="grid grid-cols-12 gap-4 md:gap-6" data-testid="dashboard-grid">
          <GlobeVisualization vesselData={vesselData} layers={layers} />
          <WeatherPanel />
          <SonarPanel />
          <MissionSummary />
          <CrewLogs />
          <AlertsPanel />
          <ChartsPanel />
        </div>
      </main>
    </div>
  );
};

export default Dashboard;
